import { useEffect, useState } from "react";
import {
  shareableLinksService,
  ProductShareMetadata,
  ProductShareAggregation,
  ShareComposition,
  ShareBasketDetail,
  ContractLite,
  OptionChainLite,
  ProductShareChangeCount,
  ProductShareProviderSummary,
  LegacyProductShareRiskMetrics,
} from "../services/shareableLinksService";
import type { ProductRiskMetrics } from "../types/productRiskMetrics";

export interface ResolvedStock {
  conId: number;
  symbol: string;
  longName: string;
  weight: number;
  secType: string;
  currency: string;
  primaryExchange: string | null;
}

export interface ResolvedOption {
  conId: number;
  symbol: string;
  strikePrice: string;
  expiryDateLocal: string;
  putCall: "P" | "C";
  weight: number;
}

export interface ResolvedBasketDetail {
  basketId: number;
  weight: number;
  visibility: "PUBLIC" | "PRIVATE" | "SUBSCRIBER";
  stocks: ResolvedStock[];
}

export interface UseProductSharePageResult {
  metadata: ProductShareMetadata | null;
  aggregation: ProductShareAggregation | null;
  stocks: ResolvedStock[];
  options: ResolvedOption[];
  basketDetails: ResolvedBasketDetail[];
  lastModifiedAtUtc: string | null;
  numOfChanges: ProductShareChangeCount[];
  riskMetrics: ProductRiskMetrics | null;
  numOfLikes: number;
  numOfComments: number;
  providerSummary: ProductShareProviderSummary;
  isLoading: boolean;
  error: string | null;
  errorType: "not-found" | "load-failed" | null;
}

function buildResolvedStocks(
  entries: { conId: number; weight: number }[],
  contractMap: Map<number, ContractLite>
): ResolvedStock[] {
  return entries.map((e) => {
    const c = contractMap.get(e.conId);
    return {
      conId: e.conId,
      symbol: c?.symbol ?? `${e.conId}`,
      longName: c?.longName ?? "",
      weight: Number(e.weight),
      secType: c?.secType ?? "STK",
      currency: c?.currency ?? "",
      primaryExchange: c?.primaryExchange ?? null,
    };
  });
}

function normalizeRiskMetrics(
  value: ProductRiskMetrics | LegacyProductShareRiskMetrics | null | undefined
): ProductRiskMetrics | null {
  if (!value) return null;
  if ("analyses" in value) return value;

  const benchmark = value.benchmarks?.[0];
  return {
    computedAtUtc: value.computedAtUtc,
    params: value.params,
    analyses: [
      {
        windowStartUtc: value.windowStartUtc,
        windowEndUtc: value.windowEndUtc,
        metrics: value.metrics,
        benchmark: benchmark
          ? {
              conId: benchmark.conId,
              symbol: benchmark.symbol,
              metrics: benchmark.metrics,
            }
          : undefined,
      },
    ],
  };
}

export function useProductSharePage(token: string | undefined): UseProductSharePageResult {
  const [metadata, setMetadata] = useState<ProductShareMetadata | null>(null);
  const [aggregation, setAggregation] = useState<ProductShareAggregation | null>(null);
  const [stocks, setStocks] = useState<ResolvedStock[]>([]);
  const [options, setOptions] = useState<ResolvedOption[]>([]);
  const [basketDetails, setBasketDetails] = useState<ResolvedBasketDetail[]>([]);
  const [lastModifiedAtUtc, setLastModifiedAtUtc] = useState<string | null>(null);
  const [numOfChanges, setNumOfChanges] = useState<ProductShareChangeCount[]>([]);
  const [riskMetrics, setRiskMetrics] = useState<ProductRiskMetrics | null>(null);
  const [numOfLikes, setNumOfLikes] = useState(0);
  const [numOfComments, setNumOfComments] = useState(0);
  const [providerSummary, setProviderSummary] = useState<ProductShareProviderSummary>({
    numOfPortfolios: 0,
    numOfBaskets: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<"not-found" | "load-failed" | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid share link");
      setErrorType("not-found");
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);
      setErrorType(null);

      try {
        const response = await shareableLinksService.getProductShareData(token!);
        if (cancelled) return;

        const {
          metadata: meta,
          aggregation: agg,
          composition,
          baskets,
          lastModifiedAtUtc: modifiedAt,
          numOfChanges: changes,
          riskMetrics: shareRiskMetrics,
          numOfLikes: likes,
          numOfComments: comments,
          provider,
        } = response.data;
        setMetadata(meta);
        setAggregation(agg);
        setLastModifiedAtUtc(modifiedAt ?? null);
        setNumOfChanges(changes ?? []);
        setRiskMetrics(normalizeRiskMetrics(shareRiskMetrics));
        setNumOfLikes(likes ?? 0);
        setNumOfComments(comments ?? 0);
        setProviderSummary(provider ?? { numOfPortfolios: 0, numOfBaskets: 0 });

        if (!composition) {
          setStocks([]);
          setOptions([]);
          setBasketDetails(baskets?.map((b) => ({
            basketId: b.basketId,
            weight: 0,
            visibility: b.visibility,
            stocks: [],
          })) ?? []);
          setIsLoading(false);
          return;
        }

        await resolveComposition(composition, baskets ?? [], meta);
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError("This share link is no longer available.");
          setErrorType("not-found");
        } else {
          console.error("Failed to load product share:", err);
          setError("Failed to load product data");
          setErrorType("load-failed");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    async function resolveComposition(
      composition: ShareComposition,
      baskets: ShareBasketDetail[],
      _meta: ProductShareMetadata
    ) {
      const allStockConIds = new Set<number>();
      const allOptionConIds: number[] = [];

      composition.contracts.stocks.forEach((s) => allStockConIds.add(s.conId));
      composition.contracts.options?.forEach((o) => allOptionConIds.push(o.conId));

      baskets.forEach((b) => {
        b.composition?.contracts.stocks.forEach((s) => allStockConIds.add(s.conId));
      });

      const [resolvedContracts, resolvedOptions] = await Promise.all([
        allStockConIds.size > 0
          ? shareableLinksService.getPublicContracts(Array.from(allStockConIds))
          : Promise.resolve([]),
        allOptionConIds.length > 0
          ? shareableLinksService.getPublicOptionChains(allOptionConIds)
          : Promise.resolve([]),
      ]);

      if (cancelled) return;

      const contractMap = new Map<number, ContractLite>();
      resolvedContracts.forEach((c) => contractMap.set(c.conId, c));

      const optionMap = new Map<number, OptionChainLite>();
      resolvedOptions.forEach((o) => optionMap.set(o.conId, o));

      setStocks(buildResolvedStocks(composition.contracts.stocks, contractMap));

      setOptions(
        (composition.contracts.options ?? []).map((o) => {
          const resolved = optionMap.get(o.conId);
          return {
            conId: o.conId,
            symbol: resolved?.symbol ?? `${o.conId}`,
            strikePrice: resolved?.strikePrice ?? "",
            expiryDateLocal: resolved?.expiryDateLocal ?? "",
            putCall: resolved?.putCall ?? "C",
            weight: Number(o.weight),
          };
        })
      );

      setBasketDetails(
        baskets.map((b) => ({
          basketId: b.basketId,
          weight: Number(composition.baskets?.find((ref) => ref.basketId === b.basketId)?.weight ?? 0),
          visibility: b.visibility,
          stocks: b.composition
            ? buildResolvedStocks(b.composition.contracts.stocks, contractMap)
            : [],
        }))
      );
    }

    load();
    return () => { cancelled = true; };
  }, [token]);

  return {
    metadata,
    aggregation,
    stocks,
    options,
    basketDetails,
    lastModifiedAtUtc,
    numOfChanges,
    riskMetrics,
    numOfLikes,
    numOfComments,
    providerSummary,
    isLoading,
    error,
    errorType,
  };
}
